import React, { useEffect, useRef, useState } from 'react';

function LazyImage({
  src,
  fallbackSrc = '',
  alt,
  className = '',
  wrapperClassName = '',
  onClick,
  ariaLabel,
}) {
  const containerRef = useRef(null);
  const [shouldLoad, setShouldLoad] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const [currentSrc, setCurrentSrc] = useState(src);

  useEffect(() => {
    setCurrentSrc(src);
    setLoaded(false);
    setFailed(false);
  }, [src, fallbackSrc]);

  useEffect(() => {
    const node = containerRef.current;

    if (!node) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setShouldLoad(true);
            observer.disconnect();
          }
        });
      },
      { rootMargin: '150px' }
    );

    observer.observe(node);

    return () => observer.disconnect();
  }, []);

  const handleError = () => {
    if (fallbackSrc && currentSrc !== fallbackSrc) {
      setCurrentSrc(fallbackSrc);
      setLoaded(false);
      return;
    }

    setFailed(true);
    setLoaded(true);
  };

  const WrapperTag = onClick ? 'button' : 'div';

  return (
    <WrapperTag
      ref={containerRef}
      type={onClick ? 'button' : undefined}
      className={`lazy-image-shell ${wrapperClassName}`.trim()}
      onClick={onClick}
      aria-label={ariaLabel || alt}
    >
      {!loaded && <div className="lazy-image-skeleton" aria-hidden="true" />}
      {shouldLoad && currentSrc && (
        <img
          src={currentSrc}
          alt={alt}
          className={`lazy-image ${loaded ? 'is-loaded' : ''} ${failed ? 'is-failed' : ''} ${className}`.trim()}
          loading="lazy"
          onLoad={() => setLoaded(true)}
          onError={handleError}
        />
      )}
      {failed && (
        <div className="lazy-image-fallback" aria-hidden="true">
          Preview unavailable
        </div>
      )}
    </WrapperTag>
  );
}

export default LazyImage;
